import jsPDF from 'jspdf';
import 'jspdf-autotable';

const DOMAIN_LABELS = {
  governance: 'Governance',
  mens: 'Mens',
  proces: 'Proces',
  techniek: 'Techniek',
  risicobeheersing: 'Risicobeheersing',
  continuiteit: 'Continuïteit',
};

export const generateAssessmentPDF = (reportData) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let y = 25;

  const org = reportData.organization || {};
  const scores = reportData.scores || {};

  // Header
  doc.setFillColor(0, 0, 0);
  doc.rect(0, 0, pageWidth, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Nulmetingsrapport', margin, 20);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(org.name || 'Organisatie', margin, 30);
  doc.text(`Gegenereerd: ${new Date(reportData.generated_at).toLocaleDateString('nl-NL')}`, pageWidth - margin, 30, { align: 'right' });

  y = 50;
  doc.setTextColor(0, 0, 0);

  // Organization info
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Organisatiegegevens', margin, y);
  y += 8;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const orgLines = [
    `Naam: ${org.name || '—'}`,
    `Adres: ${org.address || '—'}, ${org.postal_code || ''} ${org.city || ''}`,
    `Sector: ${org.sector || '—'}`,
    `Omvang: ${org.size || '—'} medewerkers`,
    `Rapporteur: ${reportData.generated_by || '—'}`,
  ];
  orgLines.forEach(line => { doc.text(line, margin, y); y += 5; });
  y += 5;

  // Total Status
  doc.setFillColor(245, 245, 245);
  doc.roundedRect(margin, y, pageWidth - 2 * margin, 25, 3, 3, 'F');
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  const status = scores.total_status?.status || 'onvoldoende_gegevens';
  const statusLabels = { kritiek: 'KRITIEK', onvoldoende_gegevens: 'ONVOLDOENDE GEGEVENS', aandacht: 'AANDACHT VEREIST', gezond: 'GEZOND' };
  doc.text(`Totaalstatus: ${statusLabels[status] || status}`, margin + 5, y + 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Betrouwbaarheid: ${Math.round(scores.confidence?.general || 0)}%`, margin + 5, y + 18);
  y += 32;

  // 5 Core Scores
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Kernscores', margin, y);
  y += 3;

  const scoreRows = [
    ['Organisatierisico', scores.risk?.org_score != null ? `${Math.round(scores.risk.org_score)} (${scores.risk.org_class})` : '—', '0-100, doel ≤20'],
    ['Volwassenheid (CMM)', scores.maturity?.org_level != null ? `Niveau ${scores.maturity.org_level}` : '—', '1-5, doel ≥3'],
    ['Volwassenheidsindex', scores.maturity?.maturity_index != null ? `${scores.maturity.maturity_index}` : '—', 'Gewogen gemiddelde'],
    ['Betrouwbaarheid', `${Math.round(scores.confidence?.general || 0)}%`, 'Doel ≥85%'],
    ['Maatregeldekking', scores.coverage?.formal_coverage != null ? `${Math.round(scores.coverage.formal_coverage)}%` : '—', 'Doel ≥90%'],
    ['Voortgang maatregelen', scores.coverage?.progress != null ? `${Math.round(scores.coverage.progress)}%` : '—', 'Gewogen naar kritiek'],
  ];

  doc.autoTable({
    startY: y,
    head: [['Score', 'Waarde', 'Schaal / Doel']],
    body: scoreRows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
    alternateRowStyles: { fillColor: [250, 250, 250] },
  });
  y = doc.lastAutoTable.finalY + 10;

  // Domain levels
  if (y > 240) { doc.addPage(); y = 25; }
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Volwassenheid per domein', margin, y);
  y += 3;

  const domainRows = Object.entries(scores.maturity?.domain_levels || {}).map(([dk, level]) => {
    const progress = scores.measure_progress?.[dk];
    return [
      DOMAIN_LABELS[dk] || dk,
      level != null ? `Niveau ${level}` : '—',
      progress ? `${progress.implemented}/${progress.total} (${progress.percentage}%)` : '—',
    ];
  });

  doc.autoTable({
    startY: y,
    head: [['Domein', 'CMM Niveau', 'Maatregelen']],
    body: domainRows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
    alternateRowStyles: { fillColor: [250, 250, 250] },
  });
  y = doc.lastAutoTable.finalY + 10;

  // Top Risks
  if (reportData.top_risks?.length > 0) {
    if (y > 200) { doc.addPage(); y = 25; }
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Top risico\'s', margin, y);
    y += 3;

    const riskRows = reportData.top_risks.map(r => [
      r.title,
      r.risk_level || '—',
      `${r.probability || '?'} × ${r.impact || '?'}`,
      r.owner || '—',
    ]);

    doc.autoTable({
      startY: y,
      head: [['Risico', 'Niveau', 'K × I', 'Eigenaar']],
      body: riskRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      columnStyles: { 0: { cellWidth: 60 } },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  // Measure summary
  if (reportData.measure_stats) {
    if (y > 240) { doc.addPage(); y = 25; }
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Maatregelen overzicht', margin, y);
    y += 3;

    const ms = reportData.measure_stats;
    const mRows = [
      ['Gepland', String(ms.planned || 0)],
      ['In uitvoering', String(ms.in_progress || 0)],
      ['Geïmplementeerd', String(ms.implemented || 0)],
      ['Geverifieerd', String(ms.verified || 0)],
      ['Totaal', String((ms.planned || 0) + (ms.in_progress || 0) + (ms.implemented || 0) + (ms.verified || 0))],
    ];

    doc.autoTable({
      startY: y,
      head: [['Status', 'Aantal']],
      body: mRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [250, 250, 250] },
    });
  }

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Spooker Nulmetingsrapport — ${org.name || ''} — Pagina ${i}/${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
    doc.text('Vertrouwelijk', pageWidth - margin, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
  }

  doc.save(`Spooker_Nulmetingsrapport_${(org.name || 'Organisatie').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
};
